/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef } from 'react';
import { useUser } from '@reducers/user/hooks';
import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';
import _isEmpty from 'lodash/isEmpty';
import { useCurrentCourse } from '@reducers/courses/hooks';
import { shutdown, onUnreadCountChange, Intercom } from '@intercom/messenger-js-sdk';
import { Organization, uniqueMap } from './utils';
import { getConversationByEmail, updateConversation, useGetIntercomToken } from './services';

const AnalyticsSetUp = () => {
  const user = useUser();
  const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID ?? '';
  const course = useCurrentCourse();
  const analyticsEnabled = process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging';
  const { data: intercomToken, isLoading } = useGetIntercomToken(user?.id, analyticsEnabled);
  const bootedRef = useRef(false);

  useEffect(() => {
    if (_isEmpty(user) || !analyticsEnabled) {
      return;
    }

    const org = (user.organizations || []) as Array<Organization>;

    // this analytics for datadog
    datadogRum.setUser({
      id: user.id,
      accountType: user.role,
      organizationName: uniqueMap(org, (o) => o.name),
      organizationId: uniqueMap(org, (o) => o.id),
      districtName: uniqueMap(org, (o) => o.district?.name),
      districtId: uniqueMap(org, (o) => o.district?.id),
      courseName: course.name,
      courseId: course.id,
    });

    // this analytics for sentry
    Sentry.setUser({ id: user.id });

    const { token } = intercomToken ?? {};
    if (!intercomAppId || isLoading || !token || bootedRef.current) {
      return;
    }

    // this analytics for intercom
    Intercom({
      app_id: intercomAppId,
      intercom_user_jwt: token,
      courseName: course.name,
      organizationName: org[0]?.name,
      organizationId: org[0]?.id,
      districtId: org[0]?.district?.id,
      districtName: org[0]?.district?.name,
      districts: uniqueMap(org, (o) => ({
        id: o.district?.id,
        name: o.district?.name,
      })),
    });
    bootedRef.current = true;

    // update analytics for intercom
    onUnreadCountChange(async () => {
      datadogRum.startSessionReplayRecording({ force: true });
      const { id: responseConversationId, datadogSessionUrl } = await getConversationByEmail(user.email);

      if (datadogRum.getSessionReplayLink() && !datadogSessionUrl && !!responseConversationId) {
        await updateConversation(responseConversationId, datadogRum.getSessionReplayLink() || '');
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, course, intercomToken, isLoading, analyticsEnabled]);

  useEffect(() => {
    if (_isEmpty(user) && bootedRef.current) {
      shutdown();
      bootedRef.current = false;
    }
    return () => {
      if (bootedRef.current) {
        shutdown();
        bootedRef.current = false;
      }
    };
  }, [user]);

  return null;
};

export default React.memo(AnalyticsSetUp);
